import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex min-h-screen flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8" }
const _hoisted_2 = { class: "sm:mx-auto sm:w-full sm:max-w-md" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "mt-8 sm:mx-auto sm:w-full sm:max-w-md" }
const _hoisted_5 = { class: "bg-white px-4 py-8 shadow sm:rounded-lg sm:px-10" }
const _hoisted_6 = {
  key: 0,
  class: "rounded-md bg-red-50 p-4 my-2"
}
const _hoisted_7 = { class: "flex" }
const _hoisted_8 = { class: "shrink-0" }
const _hoisted_9 = { class: "ml-3" }
const _hoisted_10 = { class: "text-sm font-medium text-red-800" }
const _hoisted_11 = { class: "text-sm text-yellow-900 text-right" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "flex items-center gap-3 text-sm text-gray-400" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { class: "border-t flex-1" }, null, -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("div", { class: "border-t flex-1" }, null, -1)
const _hoisted_18 = {
  key: 0,
  class: "rounded-md bg-red-50 p-4"
}
const _hoisted_19 = { class: "flex" }
const _hoisted_20 = { class: "shrink-0" }
const _hoisted_21 = { class: "ml-3" }
const _hoisted_22 = { class: "text-sm font-medium text-red-800" }
const _hoisted_23 = /*#__PURE__*/_createElementVNode("img", {
  alt: "microsoft logo",
  src: "data:image/svg+xml,%3csvg%20width='21'%20height='21'%20viewBox='0%200%2021%2021'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3crect%20x='11.375'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%23FEBA08'/%3e%3crect%20x='0.875'%20y='11.375'%20width='8.75'%20height='8.75'%20fill='%2305A6F0'/%3e%3crect%20x='11.375'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%2380BC06'/%3e%3crect%20x='0.875'%20y='0.875'%20width='8.75'%20height='8.75'%20fill='%23F25325'/%3e%3c/svg%3e"
}, null, -1)

import { XCircleIcon } from "@heroicons/vue/24/solid";
import { AxiosError } from "axios";
import { Form, SubmissionHandler } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import * as yup from "yup";
import logoMultipleNoPaddingSrc from "shared/assets/imgs/logo/logo-multiple-no-padding.svg";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import { useGetReturnUrl } from "shared/composables/auth";
import { useTrackEvent } from "shared/composables/tracking";
import logger from "shared/services/logger";
import InputField from "@/components/forms/InputField.vue";
import PasswordField from "@/components/forms/PasswordField.vue";
import { useSignIn, useMicrosoftSignIn, useLoadUserData } from "@/composables/auth";
import Footer from "@/views/auth/components/Footer.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'LogIn',
  setup(__props) {

const { t } = useI18n();

const passwordAlert = ref(false);
const socialPasswordAlert = ref(false);
const isLoading = ref(false);
const router = useRouter();
const trackEvent = useTrackEvent();

const signIn = useSignIn();
const microsoftSignIn = useMicrosoftSignIn();
const loadUserData = useLoadUserData();
const getReturnUrl = useGetReturnUrl();

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email(t("authentication.validation.validation_email_invalid"))
    .required(t("authentication.validation.validation_field_required")),

  password: yup.string().required(t("authentication.validation.validation_field_required")),
});

const handleSubmitLogIn: SubmissionHandler = async (genericObject) => {
  isLoading.value = true;
  passwordAlert.value = false;

  try {
    await signIn(genericObject.email, genericObject.password);
    const returnUrl = getReturnUrl();
    router.push(returnUrl);
  } catch (error) {
    if (error instanceof AxiosError) {
      const is401 = error?.response?.status === 401;
      const is403 = error?.response?.status === 403;
      if (
        error.message !== "Incorrect username or password." &&
        error.message !== "Network error" &&
        !is403 &&
        !is401
      ) {
        logger.error("Unable to sign in", error);
      }
    }

    passwordAlert.value = true;
  } finally {
    isLoading.value = false;
  }
};

const triggerMicrosoftLogin = async () => {
  isLoading.value = true;
  passwordAlert.value = false;
  socialPasswordAlert.value = false;
  trackEvent("user_sso_microsoft_click");

  try {
    await microsoftSignIn();
    await loadUserData();
    trackEvent("user_sso_microsoft_success");
    router.push(getReturnUrl());
  } catch (error) {
    if (error !== "No current user") {
      logger.error(error);
    }
    if (error instanceof AxiosError) {
      if (
        error?.message !== "user_cancelled: User cancelled the flow." &&
        !error?.message.includes("interaction_in_progress")
      ) {
        logger.error(error);
      }
    }
    socialPasswordAlert.value = true;
  } finally {
    isLoading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("img", {
        class: "mx-auto h-12 w-auto",
        src: _unref(logoMultipleNoPaddingSrc),
        alt: "Workflow"
      }, null, 8, _hoisted_3)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(Form), {
          onSubmit: handleSubmitLogIn,
          initialValues: { email: '', password: '' },
          validationSchema: _unref(loginSchema),
          class: "flex flex-col gap-4 w-full"
        }, {
          default: _withCtx(() => [
            _createVNode(InputField, {
              name: "email",
              label: _ctx.$t('authentication.login.email')
            }, null, 8, ["label"]),
            _createVNode(PasswordField, {
              name: "password",
              label: _ctx.$t('authentication.login.password')
            }, null, 8, ["label"]),
            (passwordAlert.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      _createVNode(_unref(XCircleIcon), {
                        class: "h-5 w-5 text-red-400",
                        "aria-hidden": "true"
                      })
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("h3", _hoisted_10, _toDisplayString(_ctx.$t("authentication.login.wrong_email_or_password")), 1)
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              _createVNode(_component_router_link, {
                to: "/forgot-password",
                class: "font-sm hover:text-yellow-700"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t("authentication.login.forgot_password")), 1)
                ]),
                _: 1
              })
            ]),
            _createElementVNode("button", {
              type: "submit",
              disabled: isLoading.value,
              class: "my-4 focus:outline-none flex w-full justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
            }, [
              (isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createVNode(LoadingSpinner, {
                      size: "w-5 h-5",
                      color: "white"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(_ctx.$t("authentication.login.sign_in")), 1))
            ], 8, _hoisted_12)
          ]),
          _: 1
        }, 8, ["validationSchema"]),
        _createElementVNode("div", _hoisted_15, [
          _hoisted_16,
          _createTextVNode(" " + _toDisplayString(_ctx.$t("authentication.login.or")) + " ", 1),
          _hoisted_17
        ]),
        (socialPasswordAlert.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("div", _hoisted_20, [
                  _createVNode(_unref(XCircleIcon), {
                    class: "h-5 w-5 text-red-400",
                    "aria-hidden": "true"
                  })
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("h3", _hoisted_22, _toDisplayString(_ctx.$t("authentication.login.social_error")), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "border mt-4 px-5 py-2 rounded-lg flex gap-3 w-full justify-center",
          type: "button",
          style: {"font-family":"Montserrat-SemiBold, system-ui"},
          onClick: triggerMicrosoftLogin
        }, [
          _hoisted_23,
          _createElementVNode("span", null, _toDisplayString(_ctx.$t("authentication.login.sign_in_with_microsoft")), 1)
        ])
      ])
    ]),
    _createVNode(Footer)
  ]))
}
}

})